/* eslint-disable no-unused-vars */
import dateFns from 'date-fns';
import { formatDate } from '../utils/formatDate';
import { intervalsOverlap } from '../utils/intervalsOverlap';

/**
 * Verifica se os períodos de visita (periodsForVisit) de um profissional (A)
 * se sobrepõem aos períodos de visita de outro profissional (B).
 *
 * @param {Array<Object>} schedulingEvents - Array de objetos de agendamento.
 * Cada objeto deve ter:
 * - professionalName (string)
 * - periodsForVisit (Array<{start: string, end: string}>) - Visitas agendadas.
 * @returns {Array<string>} - Array de mensagens de erro para conflitos de agendamento de visita entre diferentes profissionais.
 */
function check(schedulingEvents) {
  const detailedErrors = [];
  const summaryErrors = [];

  // Conjunto para evitar adicionar mensagens de erro duplicadas
  const reportedConflicts = new Set();

  // =============================================================
  // 1. Iterar sobre todos os eventos (Profissional A - Visita 1)
  // =============================================================
  for (let i = 0; i < schedulingEvents.length; i++) {
    const eventA = schedulingEvents[i];
    const { formVisits, professionalName: professionalNameA, periodsForVisit: visitsA } = eventA;
    const { professionalId: professionalIdA } = formVisits;

    // =============================================================
    // 2. Iterar sobre os demais eventos (Profissional B - Visita 2)
    // =============================================================
    // Começa a partir de i + 1 para garantir que:
    // a) Um evento não seja comparado consigo mesmo.
    // b) Não haja a comparação A vs B e B vs A.
    for (let j = i + 1; j < schedulingEvents.length; j++) {
      const eventB = schedulingEvents[j];
      const { formVisits, professionalName: professionalNameB, periodsForVisit: visitsB } = eventB;
      const { professionalId: professionalIdB } = formVisits;

      // Pula, se for o mesmo profissional
      // (essa verificação é feita em _professionalScheduleConflict)
      if (professionalIdA === professionalIdB) {
        continue;
      }

      // ============================================
      // 3. Iterar sobre Visitas de A e Visitas de B
      // ============================================
      for (const visitA of visitsA) {
        const startA = new Date(visitA.start);
        const endA = new Date(visitA.end);

        for (const visitB of visitsB) {
          const startB = new Date(visitB.start);
          const endB = new Date(visitB.end);

          // ===============================================
          // 4. Verificar Sobreposição
          // ===============================================
          if (intervalsOverlap(startA, endA, startB, endB)) {
            // Gera uma chave única baseada nas datas das visitas e nos ids dos
            // profissionais.
            // As chaves devem ser ordenadas para evitar duplicidade
            // (A vs B e B vs A).
            const keyA = `${professionalIdA}|${visitA.start}|${visitA.end}`;
            const keyB = `${professionalIdB}|${visitB.start}|${visitB.end}`;
            const conflictKey = keyA < keyB ? `${keyA}_${keyB}` : `${keyB}_${keyA}`;

            if (!reportedConflicts.has(conflictKey)) {
              // Formatar a mensagem de erro
              const errorMessage =
                `Conflito de agendamento entre profissionais: ` +
                `A visita do profissional ${professionalNameA} (${formatDate(
                  visitA.start
                )} das ${dateFns.format(startA, 'HH:mm')} às ${dateFns.format(endA, 'HH:mm')}) ` +
                `se sobrepõe à visita do profissional ${professionalNameB} (${formatDate(
                  visitB.start
                )} das ${dateFns.format(startB, 'HH:mm')} às ${dateFns.format(endB, 'HH:mm')}).`;

              const aggregateErrorsMsg = `Há conflito de agenda entre os profissionais ${professionalNameA} e ${professionalNameB}.`;
              if (!summaryErrors.find(err => err === aggregateErrorsMsg)) {
                summaryErrors.push(aggregateErrorsMsg);
              }

              detailedErrors.push(errorMessage);
              reportedConflicts.add(conflictKey);
            }
          }
        }
      }
    }
  }

  return { detailedErrors, summaryErrors };
}

export { check };
